import React, { useCallback, useRef, useState } from "react";
import { useHistory, useParams } from "react-router-dom";

import { Form } from "@unform/web";
import * as Yup from "yup";
import getValidationErrors from "../../../utils/getValidationErrors";

import { useSelector } from "react-redux";

import { FormattedMessage, useIntl } from "react-intl";
import axios from "axios";

import {
  Container,
  TitleContainer,
  Title,
  Session,
  FieldsContainer,
  Horizontal,
  ActionContainer,
  BadgeContainer,
} from "./styles";

import { useToast } from "../../../hooks/toast";
import { validateSingleFieldOnBlur } from "../../../utils/formValidations";
import api from "../../../service";
import AnimatedMainContainer from "../../../components/AnimatedMainContainer";
import InputWithHorizontalLabel from "../../../components/InputWithHorizontalLabel";
import SelectWithLabel from "../../../components/SelectWithLabel";
import Badge from "../../../components/Badge";
import Toggle from "../../../components/Toggle";
import { useEffect } from "react";
import { useLoading } from "../../../hooks/loading";

const EditAdditionalProduct = () => {
  const formRef = useRef(null);
  const intl = useIntl();
  const { addToast } = useToast();
  const { token } = useSelector((state) => state.auth);
  var language = useSelector((state) => state.language).code;
  const { id } = useParams();

  const [data, setData] = useState();
  const [categories, setCategories] = useState();
  const [categoryItem, setCategoryItem] = useState();
  const [products, setProducts] = useState();
  const [dependentProductItem, setDependentProductItem] = useState();

  const { handleLoading } = useLoading();
  const [loading, setLoading] = useState(false);

  const [formKey, setFormKey] = useState();

  const history = useHistory();

  const schema = Yup.object().shape({
    category: Yup.string().required(
      intl.formatMessage({ id: "error.field_required" })
    ),
    dependentProduct: Yup.string(),
    title: Yup.string().required(
      intl.formatMessage({ id: "error.field_required" })
    ),
    englishTitle: Yup.string().required(
      intl.formatMessage({ id: "error.field_required" })
    ),
    description: Yup.string().required(
      intl.formatMessage({ id: "error.field_required" })
    ),
    englishDescription: Yup.string().required(
      intl.formatMessage({ id: "error.field_required" })
    ),
    code: Yup.string()
      .matches(
        /^(^$|([0-9]*))$/g,
        intl.formatMessage({ id: "error.need_to_be_number" })
      )
      .max(9, intl.formatMessage({ id: "error.max_length" }, { length: 9 }))
      .required(intl.formatMessage({ id: "error.field_required" })),
  });

  useEffect(() => {
    handleLoading(true);
    const config = {
      headers: {
        "Content-Type": "application/json",
        Accept: "*/*",
        Authorization: `Bearer ${token}`,
      },
    };
    axios
      .all([
        api.get(`/admin/ecommerce/chleba/product/${id}`, config),
        api.patch(`/admin/ecommerce/chleba/product`, { status: true }, config),
        api.patch(`/admin/ecommerce/chleba/category`, {}, config),
      ])
      .then(
        axios.spread(
          (productResponse, productsResponse, categoriesResponse) => {
            setData(productResponse.data.ecommerceChlebaProductOutput);

            const products =
              productsResponse.data.ecommerceChlebaProductFilterOutput.reduce(
                (products, product) => {
                  if (product.id !== id) {
                    const { title, englishTitle, id } = product;
                    return [
                      ...products,
                      {
                        label: language === "pt" ? title : englishTitle,
                        value: id,
                      },
                    ];
                  }
                  return products;
                },
                []
              );
            setProducts(products);

            const categories =
              categoriesResponse.data.ecommerceChlebaCategoryFilterOutput.map(
                (category) => ({
                  label: category.title,
                  value: category.id,
                })
              );
            setCategories(categories);
          }
        )
      )
      .catch((err) =>
        addToast({
          type: "error",
          title: intl.formatMessage({
            id: "error",
          }),
          description: intl.formatMessage({
            id: "api.error." + err.response.data.code,
          }),
        })
      )
      .finally(() => handleLoading(false));
  }, [addToast, id, intl, token, handleLoading, language]);

  useEffect(() => {
    if (data && products) {
      const { dependentProductId } = data;
      const selectedDependentProduct = products.find(
        (product) => product.value === dependentProductId
      );
      setDependentProductItem(selectedDependentProduct);
      setFormKey(`dependent-product-${new Date().getTime()}`);
    }
  }, [data, products]);

  useEffect(() => {
    if (data && categories) {
      const { categoryId } = data;
      const selectedCategory = categories.find(
        (category) => category.value === categoryId
      );
      setCategoryItem(selectedCategory);
      setFormKey(`category-${new Date().getTime()}`);
    }
  }, [data, categories]);

  const handleSubmit = useCallback(
    async (data) => {
      try {
        formRef.current?.setErrors({});

        setLoading(true);
        if (loading) {
          return;
        }

        await schema.validate(data, { abortEarly: false });

        api
          .put(
            `/admin/ecommerce/chleba/product/${id}`,
            {
              categoryId: data.category,
              dependentProduct: data.dependentProduct,
              title: data.title,
              englishTitle: data.englishTitle,
              description: data.description,
              englishDescription: data.englishDescription,
              code: data.code,
            },
            {
              headers: {
                "Content-Type": "application/json",
                Accept: "*/*",
                Authorization: `Bearer ${token}`,
              },
            }
          )
          .then((result) => {
            if(result.data.code === 18015)
              addToast({
                type: "warning",
                title: intl.formatMessage({
                  id: "partial_success",
                }),
                description: intl.formatMessage({
                  id: "success." + result.data.code,
                }),
              });
            else
              addToast({
                type: "success",
                title: intl.formatMessage({
                  id: "success",
                }),
                description: intl.formatMessage({
                  id: "success." + result.data.code,
                }),
              });
            history.push("/admin/additional-products");
          })
          .catch((err) =>
            addToast({
              type: "error",
              title: intl.formatMessage({
                id: "error",
              }),
              description: intl.formatMessage({
                id: "api.error." + err.response.data.code,
              }),
            })
          )
          .finally(() => setLoading(false));
      } catch (err) {
        setLoading(false);
        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);
          formRef.current?.setErrors(errors);
          return;
        }
      }
    },
    [addToast, history, id, intl, loading, schema, token]
  );

  const validationBlur = (name) => {
    let errors = formRef.current.getErrors();
    validateSingleFieldOnBlur(errors, schema, name, formRef);
  };

  return (
    <AnimatedMainContainer>
      <Container>
        <Form
          ref={formRef}
          key={formKey}
          initialData={{
            category: categoryItem,
            dependentProduct: dependentProductItem,
            title: data?.title,
            englishTitle: data?.englishTitle,
            description: data?.description,
            englishDescription: data?.englishDescription,
            code: data?.code,
          }}
          onSubmit={handleSubmit}
        >
          <TitleContainer>
            <Title>
              <FormattedMessage id="editing_additional_product" />
            </Title>
            <ActionContainer>
              <BadgeContainer
                onClick={() => history.push("/admin/additional-products")}
              >
                <Badge
                  color="#C9C9C9"
                  text={intl.formatMessage({ id: "goback" })}
                  fontSize="12px"
                />
              </BadgeContainer>
              <BadgeContainer onClick={() => formRef.current.submitForm()}>
                <Badge
                  loading={loading}
                  color="#002244"
                  text={intl.formatMessage({ id: "save" })}
                  fontSize="12px"
                />
              </BadgeContainer>
            </ActionContainer>
          </TitleContainer>
          <Session>
            <FieldsContainer>
              <Horizontal>
                <SelectWithLabel
                  name="category"
                  label={intl.formatMessage({ id: "category" })}
                  placeholder={intl.formatMessage({ id: "category" })}
                  validationBlur={validationBlur}
                  labelOrientation="horizontal"
                  labelWidth="80px"
                  options={categories}
                  required
                />
                <SelectWithLabel
                  name="dependentProduct"
                  label={intl.formatMessage({ id: "dependent_product" })}
                  placeholder={intl.formatMessage({ id: "dependent_product" })}
                  validationBlur={validationBlur}
                  labelOrientation="horizontal"
                  clearable
                  labelWidth="80px"
                  options={products}
                />
              </Horizontal>
              <Horizontal>
                <InputWithHorizontalLabel
                  name="title"
                  placeholder={intl.formatMessage({
                    id: "title",
                  })}
                  label={intl.formatMessage({
                    id: "title",
                  })}
                  validationBlur={validationBlur}
                  labelWidth="80px"
                  required
                />
                <InputWithHorizontalLabel
                  name="englishTitle"
                  placeholder={intl.formatMessage({
                    id: "title_en",
                  })}
                  label={intl.formatMessage({
                    id: "title_en",
                  })}
                  validationBlur={validationBlur}
                  labelWidth="80px"
                  required
                />
              </Horizontal>
              <InputWithHorizontalLabel
                name="description"
                placeholder={intl.formatMessage({
                  id: "description",
                })}
                label={intl.formatMessage({
                  id: "description",
                })}
                validationBlur={validationBlur}
                labelWidth="80px"
                required
              />
              <InputWithHorizontalLabel
                name="englishDescription"
                placeholder={intl.formatMessage({
                  id: "description_en",
                })}
                label={intl.formatMessage({
                  id: "description_en",
                })}
                validationBlur={validationBlur}
                labelWidth="80px"
                required
              />
              <InputWithHorizontalLabel
                name="code"
                placeholder={intl.formatMessage({
                  id: "code",
                })}
                label={intl.formatMessage({
                  id: "code",
                })}
                validationBlur={validationBlur}
                labelWidth="80px"
                required
              />
            </FieldsContainer>
          </Session>
        </Form>
      </Container>
    </AnimatedMainContainer>
  );
};

export default EditAdditionalProduct;
